import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';

import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ErrorOrigin, ErrorType } from 'src/engine-sdk';

const accessControlAllowOrigin = 'Access-Control-Allow-Origin';

// http resources in which case the error dialog should not be displayed
// e.g. user's photo url
const httpsNotToHandle: string[] = ['https://graph.microsoft.com/v1.0/me/photo'];

export const httpErrorsInterceptor: HttpInterceptorFn = (request, next) => {
  let handler = next(request);
  if (!httpsNotToHandle.some((x) => request.url.startsWith(x))) {
    handler = handler.pipe(
      catchError((error: HttpErrorResponse) => {
        return isBlobError(error)
          ? parseErrorBlob(error).pipe(switchMap((e) => throwError(createError(e))))
          : throwError(createError(error));
      }),
    );
  }

  return handler;
};

function createError(errorResponse: HttpErrorResponse) {
  const isEngineServerError = errorResponse.url && errorResponse.url.startsWith(environment.urls.BaseUrl);
  let error = {
    origin: ErrorOrigin.Server,
    payload: {},
  };
  let hasCorsHeaders = errorResponse.headers.has(accessControlAllowOrigin);
  if (isEngineServerError) {
    if (errorResponse && errorResponse.error && errorResponse.error.type) {
      switch (errorResponse.error.type) {
        case ErrorType.System:
        case ErrorType.Permission:
        case ErrorType.PluginExecution:
        case ErrorType.PluginValidation:
        case ErrorType.RequestValidation:
          error.payload = errorResponse.error;
          break;
        default:
          error.payload = {
            code: errorResponse.status,
            type: ErrorType.System,
            message: errorResponse.message,
          };
      }
    } else {
      error.payload = {
        code: errorResponse.status,
        type: ErrorType.System,
        message: errorResponse.message,
      };
    }
  } else if (!errorResponse.url && !hasCorsHeaders) {
    error.payload = {
      code: 500,
      type: ErrorType.System,
      message: 'Server connection issues. Check if your server is running and has properly configured CORS policy.',
    };
  } else {
    error.payload = {
      code: errorResponse.status,
      type: ErrorType.Unknown,
      message: errorResponse.message,
    };
  }
  return error;
}

function isBlobError(err: any) {
  return err instanceof HttpErrorResponse && err.error instanceof Blob && err.error.type === 'application/json';
}

function parseErrorBlob(err: HttpErrorResponse): Observable<any> {
  const reader: FileReader = new FileReader();
  const obs = new Observable((observer: any) => {
    reader.onloadend = () => {
      const newError = {
        ...err,
        error: JSON.parse(reader.result as string),
      };

      observer.next(newError);
      observer.complete();
    };
  });
  reader.readAsText(err.error);

  return obs;
}
